import { getCardSizeSpec, isContentDrivenCard } from "./cardSizes";
import { compactItems } from "./geometry";
import { pixelsToGridRows } from "./grid";
import {
    DEFAULT_CHART_PLOT_HEIGHT_PX,
    GRID_COLUMNS,
    MINIMUM_CHART_PLOT_HEIGHT_PX,
} from "./layoutConfig";
import type {
    LayoutItem,
    LayoutObject,
    SignalsDashboardLayoutV1,
} from "./types";

/** A card's measured height, in pixels, keyed by object id. */
export type MeasuredHeights = ReadonlyMap<string, number>;

export type HeightInputs = {
    /** Natural content height of content-driven cards. */
    content: MeasuredHeights;
    /** Height of everything in a chart card that is not the plot. */
    chrome: MeasuredHeights;
    /** Cards the user has never resized, which fit their content. */
    autoHeightIds: ReadonlySet<string>;
};

/** The sizes the renderer, the keyboard and the resize outline may give a card. */
export type SizeConstraints = {
    minWidth: number;
    maxWidth: number;
    minHeight: number;
    maxHeight?: number;
};

export function resolveMinimumHeight(
    object: LayoutObject,
    inputs: HeightInputs,
): number {
    const spec = getCardSizeSpec(object);

    if (isContentDrivenCard(object.objectType)) {
        const measured = inputs.content.get(object.id);
        if (measured === undefined) return spec.minimumHeight;
        return Math.max(spec.minimumHeight, pixelsToGridRows(measured));
    }

    const chrome = inputs.chrome.get(object.id);
    if (chrome === undefined) return spec.minimumHeight;
    return Math.max(
        spec.minimumHeight,
        pixelsToGridRows(chrome + MINIMUM_CHART_PLOT_HEIGHT_PX),
    );
}

/**
 * A card's size limits. A maximum height never undercuts the content floor:
 * capping a card below its content would clip it.
 */
export function resolveSizeConstraints(
    object: LayoutObject,
    inputs: HeightInputs,
): SizeConstraints {
    const spec = getCardSizeSpec(object);
    const minHeight = resolveMinimumHeight(object, inputs);

    return {
        minWidth: spec.minimumWidth,
        maxWidth: Math.min(spec.maximumWidth ?? GRID_COLUMNS, GRID_COLUMNS),
        minHeight,
        maxHeight: spec.heightFollowsContent
            ? minHeight
            : spec.maximumHeight === undefined
            ? undefined
            : Math.max(spec.maximumHeight, minHeight),
    };
}

export function resolveSizeConstraintsById(
    objects: readonly LayoutObject[],
    inputs: HeightInputs,
): Map<string, SizeConstraints> {
    return new Map(
        objects.map((object) => [
            object.id,
            resolveSizeConstraints(object, inputs),
        ]),
    );
}

/**
 * The height a card renders at. A user-sized card keeps its height, and
 * content can only push it taller. An auto-height card fits its content; a
 * chart, which has no natural height, fits the default plot plus its chrome.
 * Until a card has been measured it keeps its stored height, so it does not
 * collapse for a frame and then jump.
 */
function resolveItemHeight(
    item: LayoutItem,
    object: LayoutObject,
    inputs: HeightInputs,
): number {
    const minimumHeight = resolveMinimumHeight(object, inputs);

    if (
        !inputs.autoHeightIds.has(item.objectId) &&
        !getCardSizeSpec(object).heightFollowsContent
    ) {
        return Math.max(item.height, minimumHeight);
    }

    if (isContentDrivenCard(object.objectType)) {
        return inputs.content.has(item.objectId) ? minimumHeight : item.height;
    }

    const chrome = inputs.chrome.get(item.objectId);
    if (chrome === undefined) return item.height;
    return Math.max(
        minimumHeight,
        pixelsToGridRows(chrome + DEFAULT_CHART_PLOT_HEIGHT_PX),
    );
}

/** The stored layout with measured heights applied, then compacted. */
export function resolveRenderedLayout(
    layout: SignalsDashboardLayoutV1,
    objects: readonly LayoutObject[],
    inputs: HeightInputs,
): SignalsDashboardLayoutV1 {
    const objectById = new Map(objects.map((object) => [object.id, object]));

    let anyHeightChanged = false;
    const resolvedItems = layout.items.map((item) => {
        const object = objectById.get(item.objectId);
        if (object === undefined) return item;

        const height = resolveItemHeight(item, object, inputs);
        if (height === item.height) return item;

        anyHeightChanged = true;
        return { ...item, height };
    });

    if (!anyHeightChanged) return layout;

    return {
        ...layout,
        items: compactItems(resolvedItems, layout.columns),
    };
}
